import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectReplaySettings, selectReplaySpeed } from '@redux/replay/replay.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { EPSILON } from '@utils/common.utils';
import Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { PlayerProps } from './Player';

const LATENCY_MARGIN = 8_000;
const BASE_SEQUENCE_DURATION = 60_000;
const LIVE_DELAY = 2_000;

function computeNimbleReplayUrl(replayUrl: string, playTime: number, sequenceDuration: number) {
  return `${replayUrl.replace('ws', 'http')}/playlist_dvr_range-${playTime
    .toString()
    .slice(0, -3)}-${Math.round(sequenceDuration / 1000)}.m3u8`;
}

function computeStandardReplayUrl(replayUrl: string, playTime: number, sequenceDuration: number) {
  const startDate = new Date(playTime).toISOString();
  const durationSeconds = Math.round(sequenceDuration / 1000);
  return `${replayUrl}?start=${startDate}&duration=${durationSeconds}`;
}

export function ReplayPlayer(props: Readonly<PlayerProps>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const replaySettings = useAppSelector(selectReplaySettings);
  const latencyMargin = replaySettings?.videoLatencyMargin ?? LATENCY_MARGIN;
  const baseSequenceDuration = replaySettings?.videoBaseSequenceDuration ?? BASE_SEQUENCE_DURATION;

  const replayTimeString = useAppSelector((state) => selectSituationTime(state, true));
  const replayTime = (replayTimeString ? new Date(replayTimeString) : new Date()).getTime();
  const replaySpeed = useAppSelector(selectReplaySpeed);
  const isPlaying = !!replaySpeed && Math.abs(replaySpeed) > EPSILON;

  const [playTime, setPlayTime] = useState(replayTime);
  const [sequenceDuration, setSequenceDuration] = useState(baseSequenceDuration);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  function setSrc(replayUrl: string, playTime: number, sequenceDuration: number) {
    try {
      const src = props.generatedByNimble
        ? computeNimbleReplayUrl(replayUrl, playTime, sequenceDuration)
        : computeStandardReplayUrl(replayUrl, playTime, sequenceDuration);

      if (hlsRef.current && src !== videoRef.current?.src) {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
        }
        hlsRef.current.loadSource(src);
      }
    } catch (error) {
      console.error('[Player] player could not update sourceUrl', error);
    }
  }

  const pause = useCallback(() => {
    hlsRef.current?.stopLoad();
    videoRef.current?.pause();
  }, [hlsRef, videoRef]);

  const isLaggingBehind = useCallback(
    (currentTime: number) => {
      return replayTime && currentTime && playTime + currentTime + latencyMargin < replayTime;
    },
    [latencyMargin, playTime, replayTime],
  );

  const isPlayingAhead = useCallback(
    (currentTime: number) => {
      return replayTime && currentTime && playTime + currentTime - latencyMargin > replayTime;
    },
    [latencyMargin, playTime, replayTime],
  );

  const hasReachedEnd = useCallback(
    (currentTime: number) => {
      return replayTime && (replayTime > playTime + sequenceDuration || currentTime > sequenceDuration - latencyMargin);
    },
    [latencyMargin, playTime, replayTime, sequenceDuration],
  );

  const isPlayerAheadOrBehindReplayTime = useCallback(
    (currentTime: number) => {
      return (
        replayTime &&
        (playTime + currentTime + latencyMargin < replayTime || playTime + currentTime - latencyMargin > replayTime)
      );
    },
    [latencyMargin, playTime, replayTime],
  );

  useEffect(() => {
    if (!props.src || !videoRef.current) {
      return;
    }

    const videoElement = videoRef.current;

    if (hlsRef.current) {
      hlsRef.current.stopLoad();
      hlsRef.current.detachMedia();
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      hlsRef.current = new Hls({
        maxBufferSize: 30,
        maxBufferLength: 60,
        maxMaxBufferLength: 120,
        lowLatencyMode: true,
        liveSyncDuration: 10,
        liveMaxLatencyDuration: 30,
      });

      hlsRef.current.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (!props.src) {
          return;
        }
        setSrc(props.src, playTime, sequenceDuration);
      });

      hlsRef.current.attachMedia(videoElement);

      hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
        if (isPlaying) {
          videoElement.play().catch(console.log);
        }
      });
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = props.src;
      videoElement.addEventListener('loadedmetadata', () => {
        if (isPlaying) {
          videoElement.play().catch(console.log);
        }
      });
    }

    return () => {
      hlsRef.current?.stopLoad();
      hlsRef.current?.detachMedia();
      hlsRef.current?.destroy();
      hlsRef.current = null;
      videoElement.pause();
      videoElement.removeAttribute('src');
      videoElement.load();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.src, videoRef, playTime, sequenceDuration]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    if (isPlaying) {
      videoRef.current.play().catch(console.log);
    } else {
      pause();
    }
  }, [isPlaying, pause]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const currentTime = (videoElement?.currentTime ?? Date.now()) * 1000;
    if (hasReachedEnd(currentTime) || isPlayerAheadOrBehindReplayTime(currentTime)) {
      pause();
      setPlayTime(replayTime);
      setSequenceDuration(
        Date.now() - playTime < baseSequenceDuration
          ? Math.round(Date.now() - playTime) - LIVE_DELAY
          : baseSequenceDuration,
      );
    } else if (isLaggingBehind(currentTime)) {
      pause();
      if (videoElement) videoElement.currentTime = replayTime - playTime;
    } else if (isPlayingAhead(currentTime)) {
      pause();
      if (videoElement) videoElement.currentTime = Math.max(0, replayTime - playTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayTime, playTime, videoRef.current?.currentTime]);

  return props.src === null ? (
    <Center width="100%" height="100%" backgroundColor="black">
      <Text>
        <FormattedMessage id="cameras.playerCrash" />
      </Text>
    </Center>
  ) : (
    <>
      <Center width="100%" height="100%" hidden={!isLoading}>
        <Spinner size="xl" color="neutral.300" />
      </Center>
      <Box data-vjs-player>
        <video
          id={props.id}
          ref={videoRef}
          hidden={isLoading}
          controls={false}
          muted
          onCanPlay={() => setIsLoading(false)}
          onWaiting={() => {
            if (isPlaying) setIsLoading(true);
          }}
          onPlaying={() => setIsLoading(false)}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
    </>
  );
}
